import React, { useEffect, useRef, useState } from 'react';

const API_URL = 'https://api.noraneko.cc/api/v1/auth/email-verifications/confirm';
const DEEP_LINK = 'girlsbandtabi://email-verified';

type Status =
  | 'loading'
  | 'success'
  | 'error-expired'
  | 'error-already'
  | 'error-invalid'
  | 'error-server'
  | 'no-token';

const ERROR_MESSAGES: Record<string, string> = {
  'error-expired': '인증 링크가 만료되었습니다. 앱에서 재발송해주세요.',
  'error-already': '이미 인증된 이메일입니다. 앱으로 돌아가 로그인하세요.',
  'error-invalid': '유효하지 않은 인증 링크입니다.',
  'error-server': '일시적인 오류입니다. 잠시 후 다시 시도해주세요.',
};

function statusFromHttpCode(code: number): Status {
  if (code === 410) return 'error-expired';
  if (code === 409) return 'error-already';
  if (code === 400) return 'error-invalid';
  return 'error-server';
}

const VerifyPage: React.FC = () => {
  const token = new URLSearchParams(window.location.search).get('token');
  const [status, setStatus] = useState<Status>(token ? 'loading' : 'no-token');
  const called = useRef(false);

  useEffect(() => {
    if (!token || called.current) return;
    called.current = true;

    fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then((res) => {
        if (res.ok) {
          setStatus('success');
        } else {
          setStatus(statusFromHttpCode(res.status));
        }
      })
      .catch(() => setStatus('error-server'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <main
      style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '3rem 1.5rem',
        textAlign: 'center',
        color: 'var(--text)',
      }}
    >
      {status === 'loading' && (
        <div
          role="status"
          aria-label="인증 확인 중"
          style={{
            width: '2.5rem',
            height: '2.5rem',
            border: '3px solid var(--stroke)',
            borderTopColor: 'var(--accent-yellow)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }}
        />
      )}

      {status === 'success' && (
        <>
          <p style={{ fontSize: '3rem', margin: '0 0 1rem' }}>✓</p>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'clamp(1.5rem, 5vw, 2.5rem)',
              margin: '0 0 0.5rem',
            }}
          >
            인증 완료
          </h1>
          <p style={{ color: 'var(--muted)', marginBottom: '2rem' }}>
            이메일 인증이 완료되었습니다.
          </p>
          <a
            href={DEEP_LINK}
            style={{
              display: 'inline-block',
              background: 'var(--accent-yellow)',
              color: '#000',
              fontWeight: 700,
              fontSize: '0.9rem',
              letterSpacing: '0.04em',
              padding: '0.75rem 2rem',
              borderRadius: 'var(--radius)',
              textDecoration: 'none',
            }}
          >
            앱으로 돌아가기
          </a>
        </>
      )}

      {status === 'no-token' && (
        <p style={{ color: 'var(--muted)' }}>잘못된 접근입니다.</p>
      )}

      {status in ERROR_MESSAGES && (
        <p style={{ color: 'var(--muted)', maxWidth: '24rem' }}>
          {ERROR_MESSAGES[status]}
        </p>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </main>
  );
};

export default VerifyPage;
